import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inOrder, latestVersion, mergeSaved } from './cache';
import { api, ApiRequestError } from './client';
import type { VolunteerRow } from './volunteers';

/**
 * External teams (SPEC §1.1J) — PGD, GRS, clubs, the organising crew.
 *
 * A team's people are `volunteer` rows carrying `externalGroupId`, so the board
 * can assign them like anyone else; they are simply never mailed, briefed or
 * autofilled.
 *
 * Deliberately NOT `extends VolunteerRow`: `GET /external-groups` returns the
 * stored columns, not the roster's derived ones (`tagIds`, `hasToken`,
 * `confirmationStatus`, `briefingVersion`, the comms rollups). Claiming those
 * would let a caller read `undefined` through a type that says otherwise —
 * `rollupFor` reads `briefingVersion`, and would compute a briefing state for
 * something that has no briefing.
 */
export type ExternalSlotRow = Pick<
  VolunteerRow,
  | 'id'
  | 'name'
  | 'phone'
  | 'email'
  | 'lang'
  | 'note'
  | 'active'
  | 'scheduleVersion'
  | 'externalPersonName'
  | 'createdAt'
  | 'updatedAt'
> & {
  externalGroupId: string;
  externalSlotIndex: number;
};

export interface ExternalGroup {
  id: string;
  name: string;
  leaderName: string;
  leaderEmail: string | null;
  leaderPhone: string;
  note: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/** What the list returns: a team with the people it is sending. */
export interface ExternalGroupRow extends ExternalGroup {
  slots: ExternalSlotRow[];
}

function onError(err: Error) {
  notifications.show({
    color: 'red',
    title: 'Napaka',
    message:
      err instanceof ApiRequestError && err.status === 409
        ? 'Podatek je medtem spremenil nekdo drug. Osveži in poskusi znova.'
        : err.message,
  });
}

export function useExternalGroups() {
  return useQuery({
    queryKey: ['external-groups'],
    queryFn: () => api<ExternalGroupRow[]>('/external-groups'),
  });
}

export interface ExternalGroupInput {
  name: string;
  leaderName?: string;
  leaderEmail?: string | null;
  leaderPhone?: string;
  note?: string;
}

/** Every write the team screen makes, run one at a time in the order made. */
const SCOPE = 'external-groups';

export function useExternalGroupMutations() {
  const qc = useQueryClient();
  // A slot is a volunteer row and holds shifts, so a change here moves the
  // board, the dashboard's coverage and the Dnevnik along with this list.
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['external-groups'] });
    void qc.invalidateQueries({ queryKey: ['volunteers'] });
    void qc.invalidateQueries({ queryKey: ['shifts'] });
    void qc.invalidateQueries({ queryKey: ['timeline'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
    void qc.invalidateQueries({ queryKey: ['changelog'] });
  };
  const common = { onError, onSettled: invalidate };

  // The team screen commits one field per blur, so its writes routinely
  // overlap: leave "Ime in priimek", type the phone, leave that too.
  const scoped = { ...inOrder(qc, SCOPE, ['external-groups'], invalidate), onError };
  const patchList = (fn: (groups: ExternalGroupRow[]) => ExternalGroupRow[]) =>
    qc.setQueryData<ExternalGroupRow[]>(['external-groups'], (groups) => groups && fn(groups));

  const create = useMutation({
    mutationFn: (input: ExternalGroupInput) =>
      api<ExternalGroupRow>('/external-groups', { method: 'POST', body: JSON.stringify(input) }),
    ...common,
  });

  const update = useMutation({
    // `updatedAt` is read when the write's turn comes, from the list the write
    // before it has already updated — not from the render that started it.
    mutationFn: ({ id, ...patch }: Partial<ExternalGroupInput> & { id: string }) =>
      api<ExternalGroup>(`/external-groups/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ ...patch, updatedAt: latestVersion(qc, ['external-groups'], id) }),
      }),
    ...scoped,
    onSuccess: (saved, { id, ...patch }) =>
      mergeSaved<ExternalGroupRow>(qc, ['external-groups'], id, saved, Object.keys(patch)),
  });

  const setSlots = useMutation({
    mutationFn: ({ id, count }: { id: string; count: number }) =>
      api<ExternalSlotRow[]>(`/external-groups/${id}/slots`, {
        method: 'PUT',
        body: JSON.stringify({ count }),
      }),
    ...scoped,
    onSuccess: (slots, { id }) =>
      patchList((groups) => groups.map((g) => (g.id === id ? { ...g, slots } : g))),
  });

  const updateSlot = useMutation({
    mutationFn: ({
      id,
      slotId,
      ...patch
    }: {
      id: string;
      slotId: string;
      externalPersonName?: string;
      phone?: string;
    }) =>
      api<ExternalSlotRow>(`/external-groups/${id}/slots/${slotId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    ...scoped,
    onSuccess: (saved, { id }) =>
      patchList((groups) =>
        groups.map((g) =>
          g.id === id
            ? { ...g, slots: g.slots.map((s) => (s.id === saved.id ? { ...s, ...saved } : s)) }
            : g,
        ),
      ),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/external-groups/${id}`, { method: 'DELETE' }),
    ...common,
  });

  return { create, update, setSlots, updateSlot, remove };
}
